import getLogger from '../logging';

const log = getLogger('worker-queue');

let jobIdCounter = 0

type PendingJobRequest<J, R> = {
    jobId: number,
    jobRequest: J,
    promise: Promise<R>,
    resolve: (result: R) => void,
    reject: (error: Error) => void,
    timeout: number,
    timeoutId?: any,
}

export class WorkerQueue<J, R> {
    worker: Worker;

    private requestQueue: PendingJobRequest<J, R>[] = [];

    private activeRequest?: PendingJobRequest<J, R> = null;

    constructor(script: string, options?: WorkerOptions) {
        this.worker = new Worker(script, options);
        this.worker.onmessage = this.handleWorkerResult;
    }

    submit(jobRequest: J, timeout: number = 5000): [jobId: number, promise: Promise<R>] {
        let
            resolve: (result: R) => void,
            reject: (error: Error) => void;
        const
            jobId = jobIdCounter++,
            promise = new Promise<R>((resolveArg, rejectArg) => {
                resolve = resolveArg;
                reject = rejectArg;
            });

        this.requestQueue.push(
            {jobId, jobRequest, promise, resolve, reject, timeout});

        this.sendNextJobRequest();
        return [ jobId, promise ];
    }

    /**
     * Raise the priority of the given jobId so that it will be the next item
     * processed by the worker.
     */
    bump(jobId: number) {
        const i = this.requestQueue.findIndex(r => r.jobId == jobId);
        if(i > -1) {
            const [item] = this.requestQueue.splice(i, 1);
            this.requestQueue.unshift(item);
        }
    }

    /**
     * If there is no active job request and there are jobs in the queue, then
     * this will remove a job from the queue, make it the active job, and send
     * it to 
     */
    private sendNextJobRequest() {
        // We may be called when something is queued or when a request
        // terminates. In the case where there is an active request, we depend
        // on the job terminating calling this method.
        if(this.activeRequest == null && this.requestQueue.length > 0) {
            this.activeRequest = this.requestQueue.shift();

            const { timeout, jobId, jobRequest, reject } = this.activeRequest;
            if(timeout > 0) {
                this.activeRequest.timeoutId = setTimeout(() => {
                    log.error(`Job #${jobId} request:`, jobRequest);
                    reject(new Error(`Job #${jobId} timeout after ${timeout}ms`));
                    this.sendNextJobRequest();
                }, timeout);
            }

            this.worker.postMessage({
                jobId: this.activeRequest.jobId,
                jobRequest: this.activeRequest.jobRequest
            });
        }
    }

    private handleWorkerResult = (event: MessageEvent) => {
        const { jobId } = event.data;
        if(this.activeRequest?.jobId == jobId) {
            if(event.data.jobError) {
                this.activeRequest.reject(new Error(event.data.jobError));
            } else {
                this.activeRequest.resolve(event.data.jobResult);
                this.activeRequest = null;
            }
            this.sendNextJobRequest();
        } else {
            log.error('Worker queue received unexpected job result', event);
        }
    }
}
export default WorkerQueue;
